import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { t } from '../utils/translations';
import { Facebook, Youtube, Instagram } from 'lucide-react';
import { FaTiktok } from 'react-icons/fa';

const Footer = () => {
  const { language } = useLanguage();

  const socialLinks = [
    { name: 'Facebook', url: 'https://www.facebook.com/profile.php?id=61564415150178', icon: Facebook },
    { name: 'YouTube', url: 'https://www.youtube.com/@MekaneHiwetEyesusDallas', icon: Youtube },
    { name: 'Instagram', url: 'https://instagram.com/mhe_orthodox_dallas', icon: Instagram },
    { name: 'TikTok', url: 'https://tiktok.com/@mhe_tewahdo_dallas', icon: FaTiktok },
    { name: 'WhatsApp', url: 'https://chat.whatsapp.com/your-group-link', icon: 'whatsapp' }
  ];

  const quickLinks = [
    { key: 'home', path: '/' },
    { key: 'about', path: '/about' },
    { key: 'sermons', path: '/sermons' },
    { key: 'gallery', path: '/gallery' },
    { key: 'donate', path: '/donate' },
    { key: 'contact', path: '/contact' }
  ];

  return (
    <footer className="bg-gradient-to-b from-amber-50 to-amber-100 dark:from-gray-900 dark:to-gray-950 border-t border-amber-200 dark:border-gray-800 transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* About Section */}
          <div>
            <div className="flex items-center space-x-3 mb-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_188dffff-cf47-49bb-8808-5c961ec9946c/artifacts/exaakh62_channels4_profile.jpg" 
                alt="Church Logo" 
                className="h-12 w-12 rounded-full"
              />
              <div>
                <h3 className="text-lg font-bold text-amber-800 dark:text-amber-500">
                  {t(language, 'home.churchName')}
                </h3>
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  {language === 'en' ? 'Dallas, Texas' : 'ዳላስ፡ ቴክሳስ'}
                </p>
              </div>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
              {t(language, 'footer.description')}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-bold text-amber-800 dark:text-amber-500 mb-4">
              {t(language, 'footer.quickLinks')}
            </h3>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.key}>
                  <Link
                    to={link.path}
                    className="text-gray-600 dark:text-gray-400 hover:text-amber-700 dark:hover:text-amber-500 transition-colors text-sm"
                  >
                    {t(language, `nav.${link.key}`)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Connect With Us */}
          <div>
            <h3 className="text-lg font-bold text-amber-800 dark:text-amber-500 mb-4">
              {t(language, 'footer.connectWithUs')}
            </h3>
            <div className="flex space-x-4">
              {socialLinks.map((social) => {
                const Icon = social.icon;
                return (
                  <a
                    key={social.name}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-10 h-10 rounded-full ${
                      social.name === 'WhatsApp' 
                        ? 'bg-green-600 hover:bg-green-700 dark:bg-green-600 dark:hover:bg-green-500' 
                        : 'bg-amber-600 hover:bg-amber-700 dark:bg-amber-700 dark:hover:bg-amber-600'
                    } text-white flex items-center justify-center transition-all duration-300 hover:scale-110`}
                    aria-label={social.name}
                  >
                    {social.name === 'WhatsApp' ? (
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                      </svg>
                    ) : (
                      <Icon className="w-5 h-5" />
                    )}
                  </a>
                );
              })}
            </div>
            <div className="mt-6 space-y-2 text-sm text-gray-600 dark:text-gray-400">
              <p>
                <strong>{t(language, 'contact.email')}:</strong>
                <br />
                <a href="mailto:mheeotcdallas@gmail.com" className="hover:text-amber-700 dark:hover:text-amber-500">
                  mheeotcdallas@gmail.com
                </a>
              </p>
              <p>
                <strong>{t(language, 'contact.address')}:</strong>
                <br />
                8822 Angora St<br />
                Dallas, TX 75218
              </p>
            </div>
          </div>
        </div>

        {/* Copyright */}
        <div className="mt-8 pt-8 border-t border-amber-200 dark:border-gray-800 text-center">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {t(language, 'footer.copyright')}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;